import { RoutePath } from '../components/views/AppRoot/types';
import { UserPermissions } from './user';

/**
 * Maps route paths to their required permissions for access.
 * Routes without a permission requirement are accessible to all authenticated users.
 */
export const getRoutePermission = (
  pathname: string
): UserPermissions | UserPermissions[] | null => {
  // Extract base path (remove query params, hash, and dynamic segments)
  const basePath = pathname.split('?')[0].split('#')[0];

  // Map routes to their required permissions
  const routePermissionMap: Record<
    string,
    UserPermissions | UserPermissions[]
  > = {
    [RoutePath.Users]: UserPermissions.UsersView,
    [RoutePath.User]: [
      UserPermissions.UsersView,
      UserPermissions.UsersEdit,
      UserPermissions.UsersCreate,
    ],
    [RoutePath.Clients]: UserPermissions.ClientsView,
    [RoutePath.Client]: UserPermissions.ClientsView,
    [RoutePath.Roles]: UserPermissions.RolesView,
    [RoutePath.CustomFields]: [
      UserPermissions.CustomFieldsView,
      UserPermissions.CustomFieldsCreate,
      UserPermissions.CustomFieldsEdit,
      UserPermissions.CustomFieldsDelete,
    ],
    [RoutePath.SystemConfigurations]: UserPermissions.SystemConfigurationView,
    [RoutePath.ProductLibrary]: UserPermissions.ModelView,
    [RoutePath.ProductList]: UserPermissions.ModelView,
    [RoutePath.ProductDetails]: UserPermissions.ModelView,
    [RoutePath.ProductDetailsImage]: UserPermissions.ModelView,
    [RoutePath.MasterLibrarianList]: UserPermissions.ModelViewUnapproved,
    [RoutePath.UploadModels]: UserPermissions.ModelUpload,
    [RoutePath.POGChecker]: UserPermissions.PogCheckerView,
    [RoutePath.MetaData]: UserPermissions.MetaDataView,
    [RoutePath.OVR]: UserPermissions.OvrProjectsView,
    [RoutePath.OVRDetails]: UserPermissions.OvrProjectsView,
    [RoutePath.OvrFileManagement]: UserPermissions.PortalFileManagementView,
    [RoutePath.Planogram]: UserPermissions.PlanogramProjectsView,
    [RoutePath.PlanogramDetails]: UserPermissions.PlanogramProjectsView,
    [RoutePath.StoreBuilder]: UserPermissions.FebDesignsView,
    [RoutePath.StoreBuilderDetails]: UserPermissions.FebDesignsView,
    [RoutePath.FrontEndBuilder]: UserPermissions.FebDesignsView,
    [RoutePath.Feb]: UserPermissions.FebDesignsView,
    [RoutePath.Downloads]: UserPermissions.DownloadsView,
  };

  // Check exact match first
  if (routePermissionMap[basePath]) {
    return routePermissionMap[basePath];
  }

  // Check for dynamic routes (e.g., /users/:id, /clients/:id)
  if (basePath.startsWith('/users/')) {
    return [
      UserPermissions.UsersView,
      UserPermissions.UsersEdit,
      UserPermissions.UsersCreate,
    ];
  }
  if (basePath.startsWith('/clients/')) {
    return UserPermissions.ClientsView;
  }
  if (basePath.startsWith('/product-library/')) {
    return UserPermissions.ModelView;
  }
  if (basePath.startsWith('/planogram/')) {
    return UserPermissions.PlanogramProjectsView;
  }
  if (basePath.startsWith('/store-builder/')) {
    return UserPermissions.FebDesignsView;
  }
  if (basePath.startsWith('/online-virtual-research/')) {
    return UserPermissions.OvrProjectsView;
  }
  if (basePath.startsWith('/account-settings/')) {
    // Account settings are accessible to the user themselves
    return null;
  }

  // Dashboard and other routes without specific permissions are accessible to all authenticated users
  return null;
};
